import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { Controller, FormProvider, FormState, UseFormReturn, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import isIBAN from 'validator/lib/isIBAN';
import { z } from 'zod';
import BankAccountController from '~/components/controllers/BankAccountController';
import CountryController from '~/components/controllers/CountryController';
import TextController from '~/components/controllers/TextController/TextController';
import IdentityTypeSelect from '~/components/counterparts/IdentityTypeSelect';
import CategoryComboBox from '~/components/inputs/Select/CategoryComboBox';
import Toggle from '~/components/inputs/Toggle/Toggle';
import Modal from '~/components/modals/Modal/Modal';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import SimpleModalActions from '~/components/modals/Modal/SimpleModalActions';
import Text from '~/components/typographies/Text';
import IconAlert from '~/icons/icon-alert-triangle-fill.svg?react';
import CheckIcon from '~/icons/icon-check.svg?react';
import IconInboxDocument from '~/icons/icon-inbox-document.svg?react';
import IconSend from '~/icons/icon-send.svg?react';
import { useShowPlus15PaymentTerms, useUpdateInvoicingSettings } from '~/services/company-settings';
import { useCounterpartHasDocuments } from '~/services/counterparts';
import { IdentityType } from '~/types/company-identity';
import { countryIsoCodes } from '~/types/country';
import { FlowPaymentTerms, PaymentMethod } from '~/types/flow';
import { isDefined } from '~/types/typeGuards';
import {
  requiredTaxNumber,
  requiredVatNumber,
  visibleCertifiedEmail,
  visibleDestinationCode,
  visibleTaxNumber,
  visibleVatNumber,
} from '~/utils/counterparts';
import {
  refinePostalCode,
  refineDestinationCodeCharset,
  refineLatin1,
  refineTaxNumberFormat,
  refineTaxNumberRequired,
  refineTaxOrVatRequired,
  refineVatNumberFormat,
  refineVatNumberRequired,
  validateDestinationCodeLength,
} from '~/utils/validation';
import { excludedPaymentMethods } from '~/views/invoices/builder/flows/FlowsSection';
import DefaultPaymentDateSelect from '~/views/invoices/profile/DefaultPaymentDateSelect';
import PaymentTermsPlus15Checkbox from '~/views/invoices/profile/PaymentTermsPlus15Checkbox';
import styles from './CounterpartEditorForm.module.scss';
import FlowPaymentMethodSelect from '../aside/FlowDetails/FlowPaymentMethodSelect';
import Icon from '../data-display/Icon';

const counterpartSchema = z
  .object({
    address: z.string().trim().min(1).superRefine(refineLatin1),
    addressCountry: z.enum(countryIsoCodes),
    city: z.string().trim().min(1).superRefine(refineLatin1),
    companyName: z.string().trim().min(1).superRefine(refineLatin1),
    identityType: z.nativeEnum(IdentityType),
    country: z.enum(countryIsoCodes),
    postalCode: z.string().trim().superRefine(refineLatin1),
    taxNumber: z.string().trim().superRefine(refineLatin1),
    vatNumber: z.string().trim().superRefine(refineLatin1),
    destinationCode: z.string().toUpperCase().trim().superRefine(refineDestinationCodeCharset),
    delivery: z
      .object({
        address: z.string().trim().min(1),
        city: z.string().trim().min(1),
        country: z.string().trim().min(1),
        postalCode: z.string().trim().min(1),
      })
      .nullable(),
    bankIdentifier: z
      .string()
      .trim()
      .refine(
        (value) => {
          return isEmpty(value) || isIBAN(value);
        },
        {
          params: { issue: 'invalid_format' },
        },
      ),
    account: z.string().nullable(),
    contactPerson: z.string().trim().nullable(),
    contactEmail: z.union([z.null(), z.literal(''), z.string().trim().email()]),
    issuedCategory: z.string().nullish(),
    issuedSubcategory: z.string().nullish(),
    receivedCategory: z.string().nullish(),
    receivedSubcategory: z.string().nullish(),
    paymentDate: z.nativeEnum(FlowPaymentTerms).nullable(),
    paymentMethod: z.nativeEnum(PaymentMethod).nullable(),
    certifiedEmail: z.union([z.null(), z.literal(''), z.string().trim().email()]),
  })
  .superRefine(refineTaxNumberFormat)
  .superRefine(refineVatNumberFormat)
  .superRefine(({ destinationCode, country, identityType }, ctx) => {
    const issue = validateDestinationCodeLength(destinationCode, country, identityType);
    if (issue) {
      ctx.addIssue({ ...issue, path: ['destinationCode'] });
    }
  })
  .superRefine(refineTaxOrVatRequired)
  .superRefine(refineTaxNumberRequired)
  .superRefine(refineVatNumberRequired)
  .superRefine(refinePostalCode);

export type CounterpartFormValues = z.infer<typeof counterpartSchema>;

type CounterpartField = keyof CounterpartFormValues;
const companyPartFields: CounterpartField[] = [
  'identityType',
  'taxNumber',
  'vatNumber',
  'destinationCode',
  'bankIdentifier',
  'contactPerson',
  'contactEmail',
  'certifiedEmail',
] as const;

const addressPartFields: CounterpartField[] = ['addressCountry', 'postalCode', 'city', 'address', 'delivery'] as const;

const managementPartFields: CounterpartField[] = [
  'issuedCategory',
  'issuedSubcategory',
  'receivedCategory',
  'receivedSubcategory',
] as const;

const hasErrors = (formState: FormState<CounterpartFormValues>, fields: CounterpartField[]) => {
  return Object.keys(formState.errors).some((v) => fields.includes(v as CounterpartField));
};

const hasCompanyErrors = (formState: FormState<CounterpartFormValues>) => hasErrors(formState, companyPartFields);

const hasAddressErrors = (formState: FormState<CounterpartFormValues>) => hasErrors(formState, addressPartFields);

const hasManagementErrors = (formState: FormState<CounterpartFormValues>) => hasErrors(formState, managementPartFields);

const DeliveryCheckbox: React.FC = () => {
  const { control, trigger } = useFormContext<CounterpartFormValues>();

  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });

  return (
    <Controller
      control={control}
      name="delivery"
      render={({ field }) => (
        <Toggle
          isSelected={isDefined(field.value)}
          onChange={(val: boolean) => {
            if (val) {
              field.onChange({ address: '', country: '', postalCode: '', city: '' });
              trigger();
            } else {
              field.onChange(null);
              trigger();
            }
          }}
          label={t('registered_office_is_delivery_address')}
        />
      )}
    ></Controller>
  );
};

const CompanySection: React.FC<{ hasDocuments?: boolean }> = ({ hasDocuments = false }) => {
  return (
    <>
      <MainSection hasDocuments={hasDocuments} />
      <ContactSection />
    </>
  );
};

const MainSection: React.FC<{ hasDocuments?: boolean }> = ({ hasDocuments = false }) => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });

  const { control, watch } = useFormContext<CounterpartFormValues>();
  const [country, identityType] = watch(['country', 'identityType']);

  return (
    <div className={styles.section}>
      <div className={styles.row}>
        <Controller
          control={control}
          name="identityType"
          render={({ field, fieldState }) => (
            <IdentityTypeSelect
              {...field}
              className={styles.control}
              label={t('identity_type')}
              errorMessage={fieldState.error?.message}
              isInvalid={fieldState.invalid}
            />
          )}
        />
        <CountryController
          className={styles.control}
          includeTaxFreeZones
          name="country"
          control={control}
          label={t('country')}
          required
        />
      </div>
      <div className={styles.row}>
        <TextController className={styles.control} control={control} name="companyName" label={t('name')} required />
        <TextController
          className={styles.control}
          name="bankIdentifier"
          control={control}
          label={t('bank_identifier')}
        />
      </div>
      <div className={styles.row}>
        {visibleTaxNumber(country) && (
          <TextController
            name="taxNumber"
            control={control}
            className={styles.control}
            label={t('tax_number')}
            required={requiredTaxNumber(country, identityType)}
            isReadOnly={hasDocuments}
            disabled={hasDocuments}
          />
        )}
        {visibleVatNumber(country, identityType) && (
          <TextController
            name="vatNumber"
            control={control}
            className={styles.control}
            label={t('vat_number')}
            required={requiredVatNumber(country, identityType)}
            isReadOnly={hasDocuments}
            disabled={hasDocuments}
          />
        )}
      </div>
      <div className={styles.row}>
        {visibleDestinationCode(country, identityType) && (
          <TextController
            className={styles.control}
            name="destinationCode"
            control={control}
            label={t('destination_code')}
          />
        )}
        {visibleCertifiedEmail(country) && (
          <TextController
            className={styles.control}
            name="certifiedEmail"
            control={control}
            label={t('certified_email')}
          />
        )}
      </div>
    </div>
  );
};

const AddressSection: React.FC = () => {
  return (
    <>
      <RegisteredOfficeSection />
      <DeliveryCheckbox />
      <DeliveryInfoSection />
    </>
  );
};

const RegisteredOfficeSection: React.FC = () => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });

  const { control } = useFormContext<CounterpartFormValues>();

  return (
    <div className={styles.section}>
      <Text variant="body1" weight="semibold">
        {t('registered_office')}
      </Text>

      <div className={styles.row}>
        <CountryController
          className={styles.control}
          name="addressCountry"
          control={control}
          label={t('country')}
          required
        />
        <TextController className={styles.control} name="address" control={control} label={t('address')} required />
      </div>
      <div className={styles.row}>
        <TextController className={styles.control} required name="city" control={control} label={t('city')} />
        <TextController
          className={styles.control}
          required
          name="postalCode"
          control={control}
          label={t('postal_code')}
        />
      </div>
    </div>
  );
};

const DeliveryInfoSection: React.FC = () => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });
  const { control, watch } = useFormContext<CounterpartFormValues>();
  const delivery = watch('delivery');

  if (!isDefined(delivery)) return null;

  return (
    <div className={styles.section}>
      <Text variant="body1" weight="semibold">
        {t('delivery_info')}
      </Text>

      <div className={styles.row}>
        <CountryController
          className={styles.control}
          name="delivery.country"
          control={control}
          label={t('country')}
          required
        />
        <TextController
          className={styles.control}
          name="delivery.address"
          control={control}
          label={t('address')}
          required
        />
      </div>
      <div className={styles.row}>
        <TextController className={styles.control} required name="delivery.city" control={control} label={t('city')} />
        <TextController
          className={styles.control}
          required
          name="delivery.postalCode"
          control={control}
          label={t('postal_code')}
        />
      </div>
    </div>
  );
};
const ContactSection = () => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });
  const { control } = useFormContext<CounterpartFormValues>();

  return (
    <div className={styles.section}>
      <Text variant="body1" weight="semibold">
        {t('contact')}
      </Text>
      <div className={styles.row}>
        <TextController className={styles.control} name="contactPerson" control={control} label={t('contact_person')} />
        <TextController className={styles.control} name="contactEmail" control={control} label={t('contact_email')} />
      </div>
    </div>
  );
};

const ManagementSection: React.FC = () => {
  const { t } = useTranslation('counterparts', { keyPrefix: 'modal' });

  // This toggle is a company-wide setting, not a counterpart field, so it is saved on click
  // rather than with the rest of the counterpart form.
  const plus15Enabled = useShowPlus15PaymentTerms();
  const updateInvoicingSettings = useUpdateInvoicingSettings();
  const handlePlus15Change = (checked: boolean) => updateInvoicingSettings.mutate({ showPlus15PaymentTerms: checked });

  const { control, setValue, watch } = useFormContext<CounterpartFormValues>();

  const issuedCategory = watch('issuedCategory');
  const issuedSubcategory = watch('issuedSubcategory');
  const receivedCategory = watch('receivedCategory');
  const receivedSubcategory = watch('receivedSubcategory');

  const [splitByDirection, setSplitByDirection] = useState<boolean>(
    issuedCategory !== receivedCategory || issuedSubcategory !== receivedSubcategory,
  );

  const changeSplit = (value: boolean) => {
    if (!value) {
      updateReceived(issuedCategory, issuedSubcategory);
    }
    setSplitByDirection(value);
  };

  const updateIssued = (categoryId: string | null | undefined, subcategoryId: string | null | undefined) => {
    setValue('issuedCategory', categoryId);
    setValue('issuedSubcategory', subcategoryId);
  };
  const updateReceived = (categoryId: string | null | undefined, subcategoryId: string | null | undefined) => {
    setValue('receivedCategory', categoryId);
    setValue('receivedSubcategory', subcategoryId);
  };

  return (
    <div className={styles.section}>
      <Text variant="body1" weight="semibold">
        {t('manage_documents')}
      </Text>
      <div className={styles.row}>
        <Controller
          control={control}
          name="paymentDate"
          render={({ field, fieldState }) => (
            <DefaultPaymentDateSelect
              {...field}
              className={styles.control}
              errorMessage={fieldState.error?.message}
              isInvalid={fieldState.invalid}
              label={t('payment_date')}
            />
          )}
        />
        <PaymentTermsPlus15Checkbox
          className={styles.control}
          checked={plus15Enabled}
          isDisabled={updateInvoicingSettings.isPending}
          onChange={handlePlus15Change}
        />
      </div>
      <div className={styles.row}>
        <Controller
          control={control}
          name="paymentMethod"
          render={({ field, fieldState }) => (
            <FlowPaymentMethodSelect
              {...field}
              allowEmpty
              className={clsx(styles.control, styles.small)}
              errorMessage={fieldState.error?.message}
              excludedPaymentMethods={excludedPaymentMethods}
              isInvalid={fieldState.invalid}
              label={t('payment_method')}
            />
          )}
        />
        <BankAccountController
          className={styles.control}
          name="account"
          control={control}
          label={t('payment_account')}
        />
      </div>
      <Toggle
        className={styles.toggle}
        isSelected={splitByDirection}
        label={t('toggle_split_by_direction')}
        onChange={changeSplit}
      />
      {splitByDirection ? (
        <>
          <div className={styles.directionRow}>
            <div className={styles.directionLabel}>
              <IconSend className={styles.icon} />
              {t('issued')}
            </div>
            <CategoryComboBox
              className={styles.control}
              categoryId={issuedCategory}
              label={t('category')}
              subcategoryId={issuedSubcategory}
              onChange={updateIssued}
            />
          </div>
          <div className={styles.directionRow}>
            <div className={styles.directionLabel}>
              <IconInboxDocument className={styles.icon} />
              {t('received')}
            </div>
            <CategoryComboBox
              className={styles.control}
              categoryId={receivedCategory}
              label={t('category')}
              subcategoryId={receivedSubcategory}
              onChange={updateReceived}
            />
          </div>
        </>
      ) : (
        <CategoryComboBox
          categoryId={issuedCategory}
          className={styles.control}
          label={t('category')}
          subcategoryId={issuedSubcategory}
          onChange={(categoryId, subcategoryId) => {
            updateIssued(categoryId, subcategoryId);
            updateReceived(categoryId, subcategoryId);
          }}
        />
      )}
    </div>
  );
};

interface State {
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: (form: UseFormReturn<CounterpartFormValues>) => void | Promise<void>;
  onCancel: (form: UseFormReturn<CounterpartFormValues>) => boolean;
  isConfirmDisabled: (form: UseFormReturn<CounterpartFormValues>) => boolean;
  module: React.FC;
}

const useCounterpartCreateSteps = (submit: (values: CounterpartFormValues) => Promise<void> | void) => {
  const { t } = useTranslation('counterparts');
  const [step, setStep] = useState<number>(0);

  const steps: State[] = [
    {
      confirmLabel: t('modal.next'),
      cancelLabel: t('modal.cancel'),
      onConfirm: async ({ trigger }) => {
        if (await trigger(companyPartFields)) setStep(1);
      },
      onCancel: () => true,
      isConfirmDisabled: ({ formState }) => hasCompanyErrors(formState),
      module: CompanySection,
    },
    {
      confirmLabel: t('modal.next'),
      cancelLabel: t('modal.back'),
      onConfirm: async ({ trigger }) => {
        if (await trigger(addressPartFields)) setStep(2);
      },
      onCancel: () => {
        setStep(0);
        return false;
      },
      isConfirmDisabled: ({ formState }) => hasAddressErrors(formState),
      module: AddressSection,
    },
    {
      confirmLabel: t('modal.confirm'),
      cancelLabel: t('modal.back'),
      onConfirm: ({ handleSubmit }) => {
        handleSubmit(submit)();
      },
      onCancel: () => {
        setStep(1);
        return false;
      },
      isConfirmDisabled: ({ formState }) => !formState.isValid,
      module: ManagementSection,
    },
  ] as const;

  return { step, state: steps[step]! };
};

const StepperItem = ({ step, current, label }: { step: number; current: number; label: string }) => {
  const next = step > current;
  const past = step < current;
  return (
    <div className={styles.stepperItem}>
      {past ? (
        <CheckIcon className={styles.stepperCount} />
      ) : (
        <Text
          variant="caption"
          weight="semibold"
          className={clsx(styles.stepperCount, { [styles.stepperNext!]: next })}
        >
          {step + 1}
        </Text>
      )}
      <Text element="div" variant="body2" weight="regular" className={styles.stepperLabel}>
        {label}
      </Text>
    </div>
  );
};

const StepperDivider = () => {
  return <div className={styles.stepperDivider} />;
};

const CreateCounterpartSteps = ({ step }: { step: number }) => {
  const { t } = useTranslation('counterparts');

  return (
    <div className={styles.stepperContainer}>
      <StepperItem step={0} current={step} label={t('modal.company')} />
      <StepperDivider />
      <StepperItem step={1} current={step} label={t('modal.address')} />
      <StepperDivider />
      <StepperItem step={2} current={step} label={t('modal.management')} />
    </div>
  );
};

export const CounterpartCreate = ({
  closeModal,
  defaultValues,
  submit,
}: {
  closeModal: () => void;
  defaultValues: CounterpartFormValues;
  submit: (values: CounterpartFormValues) => Promise<void> | void;
}) => {
  const { t } = useTranslation('counterparts');

  const {
    step,
    state: { onConfirm, onCancel, isConfirmDisabled, confirmLabel, cancelLabel, module: StepForm },
  } = useCounterpartCreateSteps(submit);

  const form = useForm<CounterpartFormValues>({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(counterpartSchema),
  });

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal();
    }
  };

  return (
    <Modal isOpen maxWidth={800} onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <ModalTitle onCloseClick={closeModal}>{t('modal.create_title')}</ModalTitle>
        <ModalContent className={styles.content}>
          <CreateCounterpartSteps step={step} />
          {<StepForm />}
        </ModalContent>

        <SimpleModalActions
          isConfirmDisabled={isConfirmDisabled(form)}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          onConfirm={() => onConfirm(form)}
          onCancel={() => onCancel(form)}
        />
      </FormProvider>
    </Modal>
  );
};

export const CounterpartEdit = ({
  closeModal,
  defaultValues,
  counterpartId,
  submit,
}: {
  closeModal: () => void;
  defaultValues: CounterpartFormValues;
  counterpartId: string;
  submit: (values: CounterpartFormValues) => Promise<void> | void;
}) => {
  const { t } = useTranslation('counterparts');

  const { isLoading, data: hasDocuments } = useCounterpartHasDocuments(counterpartId);

  const form = useForm<CounterpartFormValues>({
    defaultValues,
    mode: 'onBlur',
    resolver: zodResolver(counterpartSchema),
  });
  const { handleSubmit, formState } = form;

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal();
    }
  };

  return (
    <Modal isOpen fullWidth maxWidth={800} onOpenChange={handleOpenChange} isLoading={isLoading}>
      <FormProvider {...form}>
        <ModalTitle>{t('modal.edit_title')}</ModalTitle>
        <ModalContent className={styles.content}>
          <Tabs defaultSelectedKey="company" className={styles.tabs}>
            <TabList className={styles.tabContainer}>
              <TabStep label={t('modal.company')} id="company" alert={hasCompanyErrors(formState)} />
              <TabStep label={t('modal.address')} id="address" alert={hasAddressErrors(formState)} />
              <TabStep label={t('modal.management')} id="management" alert={hasManagementErrors(formState)} />
            </TabList>

            <TabPanel id="company" className={styles.tabPanel}>
              <CompanySection hasDocuments={hasDocuments} />
            </TabPanel>

            <TabPanel id="address" className={styles.tabPanel}>
              <AddressSection />
            </TabPanel>

            <TabPanel id="management" className={styles.tabPanel}>
              <ManagementSection />
            </TabPanel>
          </Tabs>
        </ModalContent>

        <SimpleModalActions
          isConfirmDisabled={!formState.isValid}
          confirmLabel={t('modal.confirm')}
          onConfirm={() => handleSubmit(submit)()}
        />
      </FormProvider>
    </Modal>
  );
};

interface TabProps {
  label: string;
  alert?: boolean;
  id: string;
}

const TabStep = ({ alert, label, id }: TabProps) => {
  return (
    <Tab className={styles.tab} id={id}>
      {label}
      {alert && <Icon component={IconAlert} className={styles.alertIcon} />}
    </Tab>
  );
};
